import { Icon } from "@mdi/react";

import { makeStyles } from "@material-ui/core/styles";
import { Palette, ShopTwo } from "@material-ui/icons";
import AccountBoxIcon from "@material-ui/icons/AccountBox";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import WorkIcon from "@material-ui/icons/Work";
import { mdiCalculator, mdiLayers, mdiMapCheck, mdiMemory, mdiTrendingUp } from "@mdi/js";
import { IS_SSO_BRIDGE } from "constants/app.constants";
import { selectActiveSetting, usePanelStore } from "stores/panelStore";
import styled from "styled-components";

import { useUser } from "hooks";
import useBetaFeatures from "hooks/useBetaFeatures";
import useCanUseGeoModels from "hooks/useCanUseGeoModels";

import { BaseButton, SubHeading } from "components/base";
import { UserArps } from "components/icons";
import AccountSecurity from "components/icons/AccountSecurity";
import DataSource from "components/icons/DataSource";
import DisplaySettings from "components/icons/DisplaySettingsIcon";

import "./UserSettingSideNav.scss";

const useStyles = makeStyles({
  navItems: {
    fontSize: "1.2em",
    textAlign: "left",
    display: "inline"
  }
});

export default function UserSettingSideNav() {
  const classes = useStyles();
  const { isAtLeastPowerUser } = useUser();
  const { hasFeature } = useBetaFeatures();
  // Power-user+ only, so don't fetch the org access list for anyone else.
  const canUseGeoModels = useCanUseGeoModels(!!isAtLeastPowerUser);
  const activeSetting = usePanelStore(selectActiveSetting);
  const { closePanel, setActiveSetting } = usePanelStore();

  return (
    <div className="UserSettingSideNav" data-testid="user-settings-side-nav">
      <ul className="settings-nav">
        <li className="back-dashboard">
          <BaseButton
            appearance="subtle"
            className={classes.navItems}
            data-testid="settings-back-to-dashboard"
            onClick={() => closePanel()}>
            <div>
              <KeyboardBackspaceIcon style={{ fontSize: "24px" }} />
            </div>
            Back to dashboard
          </BaseButton>
        </li>
        <div className="settings-section">
          <SubHeading>General</SubHeading>
          <li
            className={`settings-nav-item ${
              activeSetting === "Profile" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("Profile")}>
              <div className="inline-flex">
                <IconWrapper>
                  <AccountBoxIcon style={{ fontSize: "24px" }}></AccountBoxIcon>
                </IconWrapper>
                <span>Profile</span>
              </div>
            </BaseButton>
          </li>
          {/* Novi Auth bridge: password / phone / 2FA are managed at the edge,
              and that's everything actionable in this panel. */}
          {!IS_SSO_BRIDGE && (
            <li
              className={`settings-nav-item ${
                activeSetting === "AccountSecurity" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("AccountSecurity")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <AccountSecurity></AccountSecurity>
                  </IconWrapper>
                  <span>Account & Security</span>
                </div>
              </BaseButton>
            </li>
          )}
        </div>
        <div className="settings-section">
          <SubHeading>Integrations</SubHeading>
          <li
            className={`settings-nav-item ${
              activeSetting === "DataSource" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("DataSource")}>
              <div className="inline-flex">
                <IconWrapper>
                  <DataSource></DataSource>
                </IconWrapper>
                <span>Data Source</span>
              </div>
            </BaseButton>
          </li>
          <li
            className={`settings-nav-item ${
              activeSetting === "ThirdPartyIntegration" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("ThirdPartyIntegration")}>
              <div className="inline-flex">
                <IconWrapper>
                  <ShopTwo style={{ fontSize: "20px" }} />
                </IconWrapper>
                <span>Third Party</span>
              </div>
            </BaseButton>
          </li>
        </div>
        <div className="settings-section">
          <SubHeading>Configuration</SubHeading>
          <li
            className={`settings-nav-item ${
              activeSetting === "WorkspacesList" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("WorkspacesList")}>
              <div className="inline-flex">
                <IconWrapper>
                  <WorkIcon style={{ fontSize: "20px" }}></WorkIcon>
                </IconWrapper>
                <span>Workspaces</span>
              </div>
            </BaseButton>
          </li>
          <li
            className={`settings-nav-item ${
              activeSetting === "DeclineSettings" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("DeclineSettings")}>
              <div className="inline-flex">
                <IconWrapper>
                  <UserArps />
                </IconWrapper>

                <span>Decline Settings</span>
              </div>
            </BaseButton>
          </li>
          {isAtLeastPowerUser && (
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationForecasts" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                data-testid={"org-forecast-navigation"}
                onClick={() => setActiveSetting("OrganizationForecasts")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiTrendingUp} size={1.5} />
                  </IconWrapper>
                  <span>Organization Forecasts</span>
                </div>
              </BaseButton>
            </li>
          )}
          {isAtLeastPowerUser && (
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationShapefiles" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                onClick={() => setActiveSetting("OrganizationShapefiles")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiLayers} size={1.5} />
                  </IconWrapper>
                  <span>Org Shapefiles & GeoTIFFs</span>
                </div>
              </BaseButton>
            </li>
          )}
          {isAtLeastPowerUser && hasFeature("Org Geo Fields") && (
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationGeoCalculationJobs" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                data-testid={"geo-model-calculations-navigation"}
                onClick={() => setActiveSetting("OrganizationGeoCalculationJobs")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiCalculator} size={1.5} />
                  </IconWrapper>
                  <span>Geo Model Calculations</span>
                </div>
              </BaseButton>
            </li>
          )}
          {isAtLeastPowerUser && canUseGeoModels && (
            <li
              className={`settings-nav-item ${
                activeSetting === "GeoModelAccess" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                data-testid={"geo-model-access-navigation"}
                onClick={() => setActiveSetting("GeoModelAccess")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiMapCheck} size={1.5} />
                  </IconWrapper>
                  <span>Geo Model Access</span>
                </div>
              </BaseButton>
            </li>
          )}
          {isAtLeastPowerUser && hasFeature("Org Geo Simulations") && (
            <li
              className={`settings-nav-item ${
                activeSetting === "OrganizationGeoSimulationJobs" ? "active" : ""
              }`}>
              <BaseButton
                appearance="subtle"
                className={classes.navItems}
                data-testid={"geo-model-simulations-navigation"}
                onClick={() => setActiveSetting("OrganizationGeoSimulationJobs")}>
                <div className="inline-flex">
                  <IconWrapper>
                    <Icon path={mdiMemory} size={1.6} />
                  </IconWrapper>
                  <span>Geo Model Simulations</span>
                </div>
              </BaseButton>
            </li>
          )}
        </div>
        <div className="settings-section">
          <SubHeading>Preferences</SubHeading>
          <li
            className={`settings-nav-item ${
              activeSetting === "DisplaySettings" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("DisplaySettings")}>
              <div className="inline-flex">
                <IconWrapper>
                  <DisplaySettings></DisplaySettings>
                </IconWrapper>
                <span> Display Settings</span>
              </div>
            </BaseButton>
          </li>
          <li
            className={`settings-nav-item ${
              activeSetting === "UserDefaults" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              data-testid="settings-nav-user-defaults"
              onClick={() => setActiveSetting("UserDefaults")}>
              <div className="inline-flex">
                <IconWrapper>
                  <SettingsBackupRestoreIcon
                    style={{ fontSize: "20px" }}></SettingsBackupRestoreIcon>
                </IconWrapper>
                <span>Defaults</span>
              </div>
            </BaseButton>
          </li>
          <li
            className={`settings-nav-item ${
              activeSetting === "ColorPalette" ? "active" : ""
            }`}>
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => setActiveSetting("ColorPalette")}>
              <div className="inline-flex">
                <IconWrapper>
                  <Palette style={{ fontSize: "20px" }} />
                </IconWrapper>
                <span>Colour Palettes</span>
              </div>
            </BaseButton>
          </li>
        </div>
        {/*<div className="settings-section">
          <SubHeading>Support</SubHeading>
          <li
            className={`settings-nav-item ${
              activeSetting === "/product-tour" ? "active" : ""
            }`}
          >
            <BaseButton
              appearance="subtle"
              className={classes.navItems}
              onClick={() => goTo("/product-tour")}
            >
              <div className="inline-flex">
                <SchoolIcon style={{ fontSize: "24px" }}></SchoolIcon>
                <span>
                  Product Tour <Launch></Launch>
                </span>
              </div>
            </BaseButton>
          </li>
          </div>*/}
      </ul>
    </div>
  );
}

const IconWrapper = styled.div`
  min-width: 30px;
`;
